/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  Comment,
  storyCardApi,
  StoryCardCommentsFeedByChannelIdArgs,
  StoryCardsFeedFilter,
} from '../services';
import { editComment, hideComment, highlightComment, reactOnComment } from './common';

const commentsFeedHandler = async (
  args: StoryCardCommentsFeedByChannelIdArgs,
  { dispatch }: any
) => {
  return dispatch(
    storyCardApi.endpoints.storyCardCommentsFeedByChannelId.initiate({ ...args })
  ).unwrap();
};

export const getCommentsFeed = createAsyncThunk('commentsFeed', commentsFeedHandler);

export const loadMoreCommentsFeed = createAsyncThunk('commentsFeed/loadMore', commentsFeedHandler);

interface CommentsFeedState {
  isFetched: boolean;
  commentsFeed: Comment[];
  page: number;
  hasNextPage: boolean;
  filters?: StoryCardsFeedFilter;
}

const initialState: CommentsFeedState = {
  isFetched: false,
  commentsFeed: [],
  page: 0,
  hasNextPage: false,
  filters: undefined,
};

const commentsFeedSlice = createSlice({
  name: 'commentsFeed',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCommentsFeed.pending, (state) => {
        state.isFetched = false;
      })
      .addCase(getCommentsFeed.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;
        const { page, hasNextPage } = pageInfo;

        state.isFetched = true;
        state.commentsFeed = items;
        state.page = page;
        state.hasNextPage = hasNextPage;
        state.filters = action.meta.arg.filters;
      })
      .addCase(loadMoreCommentsFeed.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;
        const { page, hasNextPage } = pageInfo;

        state.commentsFeed.push(...items);
        state.page = page;
        state.hasNextPage = hasNextPage;
      })
      .addCase(editComment.fulfilled, (state, action) => {
        const { id: commentId, content, attachments, contentUpdatedAt } = action.payload.comment;

        const commentToUpdate = state.commentsFeed.find(({ id }) => id === commentId);

        if (!commentToUpdate) {
          return;
        }

        commentToUpdate.content = content;
        commentToUpdate.attachments = attachments;
        commentToUpdate.contentUpdatedAt = contentUpdatedAt;
      })
      .addCase(hideComment.fulfilled, (state, action) => {
        const { id: commentId, parentId, isHidden } = action.payload.comment;

        const commentsToUpdate = parentId
          ? state.commentsFeed.find(({ id }) => id === parentId)?.replies
          : state.commentsFeed;

        if (!commentsToUpdate) {
          return;
        }

        const commentToUpdate = commentsToUpdate.find(({ id }) => id === commentId);

        if (!commentToUpdate) {
          return;
        }

        commentToUpdate.isHidden = isHidden;
      })
      .addCase(highlightComment.fulfilled, (state, action) => {
        const { id: commentId, isHighlighted } = action.payload.comment;

        const commentToReset = state.commentsFeed.find(({ isHighlighted }) => isHighlighted);

        if (commentToReset) {
          commentToReset.isHighlighted = false;
        }

        const commentToUpdate = state.commentsFeed.find(({ id }) => id === commentId);

        if (!commentToUpdate) {
          return;
        }

        commentToUpdate.isHighlighted = isHighlighted;
      })

      .addCase(reactOnComment.fulfilled, (state, action) => {
        const { reactions, commentId } = action.payload;

        const commentsAndReplies = state.commentsFeed.flatMap((comment) => {
          return [comment, ...comment.replies];
        });

        const commentToUpdate = commentsAndReplies.find(({ id }) => id === commentId);

        if (!commentToUpdate) {
          return;
        }

        commentToUpdate.myReaction = commentToUpdate.myReaction ? '' : 'like';
        commentToUpdate.reactions = reactions;
      });
  },
});

export default commentsFeedSlice.reducer;
